/**
 * AI image edits for hdrphoto, routed through the shared `gemini-image`
 * Supabase Edge Function (model `gemini-3-pro-image-preview`) so the Gemini API
 * key stays server-side instead of being bundled into the public JS.
 *
 * Currently used for AI sky replacement ("Remove clouds"): the model paints in a
 * clean blue sky while keeping the rest of the photo identical.
 */

export type SkyStyle = 'soft' | 'clear' | 'deep' | 'sunset' | 'twilight';

export const DEFAULT_SKY_STYLE: SkyStyle = 'clear';

/** Styles offered in the Blue Sky editor (drives the AI generation). */
export const SKY_STYLES: { id: SkyStyle; label: string; hint: string }[] = [
  { id: 'soft', label: 'Soft Blue', hint: 'Gentle pale blue' },
  { id: 'clear', label: 'Clear Blue', hint: 'Bright summer blue' },
  { id: 'deep', label: 'Deep Blue', hint: 'Rich vivid azure' },
  { id: 'sunset', label: 'Sunset', hint: 'Warm golden hour' },
  { id: 'twilight', label: 'Twilight', hint: 'Calm dusk blue hour' },
];

export const isSkyStyle = (v: any): v is SkyStyle =>
  v === 'soft' ||
  v === 'clear' ||
  v === 'deep' ||
  v === 'sunset' ||
  v === 'twilight';

const SUPABASE_URL =
  (import.meta as any).env?.VITE_SUPABASE_URL || process.env.VITE_SUPABASE_URL || '';
const SUPABASE_ANON_KEY =
  (import.meta as any).env?.VITE_SUPABASE_ANON_KEY ||
  process.env.VITE_SUPABASE_ANON_KEY ||
  '';
const FN_URL = `${SUPABASE_URL}/functions/v1/gemini-image`;

const MAX_RETRIES = 3;
const INITIAL_DELAY_MS = 2000;

const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

const isRetryableError = (msg: string): boolean =>
  /429|rate.?limit|quota|RESOURCE_EXHAUSTED|temporarily unavailable|overloaded|capacity|timed?[- ]out|interrupted|502|503|504/i.test(
    msg
  );

interface RunOpts {
  prompt: string;
  images: string[]; // data: URLs or https URLs
}

async function runGemini(opts: RunOpts): Promise<string> {
  if (!SUPABASE_URL || !SUPABASE_ANON_KEY) {
    throw new Error(
      'Image service not configured: VITE_SUPABASE_URL / VITE_SUPABASE_ANON_KEY missing.'
    );
  }

  let lastError: any;
  for (let attempt = 0; attempt < MAX_RETRIES; attempt++) {
    try {
      if (attempt > 0) {
        const delay = INITIAL_DELAY_MS * Math.pow(2, attempt - 1);
        await sleep(delay);
      }

      const r = await fetch(FN_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${SUPABASE_ANON_KEY}`,
          apikey: SUPABASE_ANON_KEY,
        },
        body: JSON.stringify({
          prompt: opts.prompt,
          images: opts.images,
          aspect_ratio: 'match_input_image',
          resolution: '4K',
          output_format: 'png',
        }),
      });

      const data = await r.json().catch(() => ({}) as any);
      if (!r.ok || !data?.imageUrl) {
        throw new Error(data?.error || `Edge function error ${r.status}`);
      }
      return data.imageUrl as string; // data: URL
    } catch (e: any) {
      lastError = e;
      const msg = e?.message || String(e);
      console.error(`Gemini attempt ${attempt + 1} failed:`, msg);
      if (!isRetryableError(msg) || attempt === MAX_RETRIES - 1) throw e;
    }
  }
  throw lastError;
}

/**
 * Per-style description of the sky we want the model to paint. These describe a
 * believable photographic sky (gradient + restrained clouds), not a flat fill —
 * flat fills are the main reason AI skies look fake.
 */
const STYLE_SKY_DESCRIPTION: Record<SkyStyle, string> = {
  soft:
    'a soft, gentle blue sky that fades from a light powder blue high up to a ' +
    'warm pale haze near the horizon, with a few thin, wispy high clouds for ' +
    'natural depth',
  clear:
    'a bright, clear summer-blue sky with a natural top-to-horizon gradient ' +
    '(deeper blue overhead easing to a lighter blue near the horizon) and just ' +
    'a few small, soft scattered clouds for realism',
  deep:
    'a deep, vivid azure sky with a rich top-to-horizon gradient and almost no ' +
    'clouds, like a crisp high-pressure day',
  sunset:
    'a warm golden-hour sunset sky: a soft gradient running from peach and ' +
    'orange near the horizon up through gentle pink to a calm deepening blue ' +
    'higher up, with a few soft clouds catching warm light',
  twilight:
    'a calm twilight "blue hour" sky shortly after sunset: a deep blue overhead ' +
    'easing down to soft indigo with a faint warm glow along the horizon — clean, ' +
    'atmospheric and natural',
};

/**
 * Replace the sky in a real-estate photo with a believable sky in the chosen
 * style.
 *
 * The model finds the sky itself — open sky in exteriors and the sky seen
 * through windows/doors/skylights in interiors — and paints a *photographic*
 * sky there (natural gradient + subtle clouds), strictly masked so colour never
 * bleeds onto frames, blinds, walls or reflections. Everything else is left
 * untouched. Returns a data: URL of the edited image.
 */
export async function replaceSky(
  imageUrl: string,
  style: SkyStyle = DEFAULT_SKY_STYLE
): Promise<string> {
  // Encode the source in the browser and send a data: URL. Passing a raw https
  // URL makes the edge function fetch + base64-encode a large 4K image itself,
  // which can exceed the Edge runtime's memory/CPU limit (HTTP 546). Doing it
  // client-side keeps the function's work cheap, matching the other apps.
  const inlineImage = await urlToDataUrl(imageUrl);
  const skyDesc = STYLE_SKY_DESCRIPTION[style] || STYLE_SKY_DESCRIPTION.clear;
  const prompt =
    `You are a professional real-estate photo retoucher performing a clean, ` +
    `realistic sky replacement.\n\n` +
    `STEP 1 — FIND THE SKY: identify every region that shows sky — the open sky ` +
    `in an exterior shot, and the sky visible through each window, glass door, ` +
    `skylight or open doorway in an interior shot.\n\n` +
    `STEP 2 — PAINT THE NEW SKY only in those regions: ${skyDesc}. It must be a ` +
    `genuine photographic sky — a smooth natural gradient with believable ` +
    `variation and soft cloud edges — NOT a flat, uniform, posterised or ` +
    `cartoonish fill.\n\n` +
    `STRICT MASKING (most important): confine the new sky strictly to true sky ` +
    `pixels. Do NOT let any sky colour, brightness or haze spill onto window ` +
    `frames, mullions, glazing bars, blinds, slats, curtains, sills, walls, ` +
    `ceilings, furniture or reflections — these keep their exact original colour ` +
    `and detail. Render crisp, photorealistic edges where sky meets rooftops, ` +
    `chimneys, aerials, trees, railings, wires and window frames (no halos, glow ` +
    `or colour fringing).\n\n` +
    `INTERIOR shots: the room stays exactly as-is. Through the glass, replace any ` +
    `blown-out, white, grey or overcast sky with the new sky and recover gentle ` +
    `over-brightness so the outside is clearly visible. PRESERVE any real view ` +
    `already there — skyline, buildings, neighbouring houses, trees, gardens, ` +
    `balconies — only the sky portion becomes the new sky. Everything in front ` +
    `of or around the glass (frames, bars, blinds, curtains, reflections) stays ` +
    `exactly as-is and in front of the glass. The outdoor view should read ` +
    `slightly brighter than the interior, as real daylight does.\n\n` +
    `EXTERIOR shots: replace the whole visible sky, removing dull grey, white or ` +
    `overcast cover and keeping the natural lightening near the horizon.\n\n` +
    `DO NOT change anything else: do not move, recolour, relight, re-expose, ` +
    `zoom, crop, distort or reshape the building, interior, furniture, fixtures, ` +
    `flooring, walls, ceilings, people, vehicles or vegetation. Keep the exact ` +
    `original framing, perspective, camera angle and aspect ratio. The final ` +
    `image must look like an unedited photograph, with no artifacts, seams or ` +
    `AI tells.`;

  return runGemini({ prompt, images: [inlineImage] });
}

/** Convert a data: URL (or any URL) to a Blob for upload. */
export async function urlToBlob(url: string): Promise<Blob> {
  const r = await fetch(url);
  if (!r.ok) throw new Error(`Failed to read generated image: ${r.status}`);
  return r.blob();
}

/**
 * Fetch a hosted image URL and convert it to a base64 data: URL so the edge
 * function receives inline data instead of having to download and encode the
 * image server-side. Pass-through if it is already a data: URL.
 */
export async function urlToDataUrl(url: string): Promise<string> {
  if (url.startsWith('data:')) return url;
  const blob = await urlToBlob(url);
  return await new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result as string);
    reader.onerror = () =>
      reject(new Error('Failed to read the image as a data URL'));
    reader.readAsDataURL(blob);
  });
}
